import is from '@sindresorhus/is';
import type { FunctionComponent, KeyboardEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { PromptComposer } from '~/ui/domain/PromptComposer/PromptComposer';

import type { CommentAnchor } from '../../../data-contract/comment';
import { TranscribableTextarea } from '../../components/TranscribableTextarea';
import { useActiveThreadStore } from './ActiveThreadStoreProvider';
import { anchorFromMarkedHtml } from './comment-anchor-markup';
import { resolveContentRoot } from './resolve-content-root';
import { useCreateComment } from './use-create-comment';

export const PENDING_COMPOSER_ID = '__pending__';

const anchorFromLiveMark = (itemId: string, commentId: string): CommentAnchor | null => {
    const contentRoot = resolveContentRoot(itemId);
    if (is.null(contentRoot)) {
        return null;
    }
    const mark = contentRoot.querySelector<HTMLElement>(`mark[data-comment-id="${commentId}"]`);
    const editorRoot = mark?.closest<HTMLElement>('[contenteditable="true"]');
    if (is.nullOrUndefined(editorRoot)) {
        return null;
    }
    return anchorFromMarkedHtml(editorRoot.innerHTML, commentId);
};

type RegisterCard = (id: string, el: HTMLElement | null) => void;

type CommentComposerProps = {
    readonly architectureId: string;
    readonly itemId: string;
    readonly version: number;
    readonly language: string;
    readonly registerCard: RegisterCard;
};

export const CommentComposer: FunctionComponent<CommentComposerProps> = ({
    architectureId,
    itemId,
    language,
    registerCard,
    version,
}) => {
    const translate = useTranslate();
    const pendingTarget = useActiveThreadStore((state) => state.pendingTarget);
    const isRailOpening = useActiveThreadStore((state) => state.isRailOpening);
    const setActiveThreadId = useActiveThreadStore((state) => state.setActiveThreadId);
    const cancelPending = useActiveThreadStore((state) => state.cancelPending);
    const clearPending = useActiveThreadStore((state) => state.clearPending);
    const createComment = useCreateComment({ architectureId, itemId, language, version });
    const [body, setBody] = useState('');
    const [isSubmitted, setIsSubmitted] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        if (isSubmitted || isRailOpening) return;
        let cancelled = false;
        const claimFocus = (): void => {
            if (cancelled) return;
            const textarea = textareaRef.current;
            if (!is.null(textarea) && document.activeElement !== textarea) {
                textarea.focus({ preventScroll: true });
            }
        };
        const timers = [0, 32, 80, 160].map((delay) => setTimeout(claimFocus, delay));
        return () => {
            cancelled = true;
            for (const timer of timers) {
                clearTimeout(timer);
            }
        };
    }, [isSubmitted, isRailOpening]);

    const trimmed = body.trim();
    const submitDisabled = trimmed.length === 0 || createComment.isPending || isSubmitted || is.null(pendingTarget);

    const handleSubmit = (): void => {
        if (trimmed.length === 0 || createComment.isPending || isSubmitted || is.null(pendingTarget)) return;
        const id = pendingTarget.commentId;
        const target = pendingTarget.target;
        const anchor =
            target.kind === 'description-section'
                ? (pendingTarget.anchor ?? anchorFromLiveMark(itemId, id))
                : undefined;
        setActiveThreadId(id);
        setIsSubmitted(true);
        createComment.mutate(
            {
                content: trimmed,
                id,
                anchor,
                target,
            },
            {
                onError: () => {
                    setActiveThreadId(null);
                    setIsSubmitted(false);
                },
                onSuccess: () => {
                    clearPending();
                    setBody('');
                    setIsSubmitted(false);
                },
            },
        );
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            handleSubmit();
            return;
        }
        if (event.key === 'Escape') {
            event.preventDefault();
            cancelPending();
            setBody('');
        }
    };

    const handleRef = useCallback(
        (el: HTMLElement | null) => {
            registerCard(PENDING_COMPOSER_ID, el);
        },
        [registerCard],
    );

    return isSubmitted ? null : (
        <div
            ref={handleRef}
            className="absolute left-0 right-0 px-s"
            data-comment-composer=""
            data-testid="comment-composer"
        >
            <PromptComposer
                cancelLabel={translate('common.cancel')}
                mode="rail"
                submitDisabled={submitDisabled}
                submitLabel={translate('pages.job-architecture-v2.comments.composer.submit')}
                submitTestId="comment-composer-submit"
                onCancel={() => {
                    cancelPending();
                    setBody('');
                }}
                onSubmit={handleSubmit}
            >
                <TranscribableTextarea
                    ref={textareaRef}
                    autoResize={true}
                    disabled={createComment.isPending || is.null(pendingTarget) || isRailOpening}
                    maxRows={4}
                    placeholder={translate('pages.job-architecture-v2.comments.composer.placeholder')}
                    rows={3}
                    value={body}
                    onKeyDown={handleKeyDown}
                    onValueChange={setBody}
                />
            </PromptComposer>
        </div>
    );
};
